// ============================================================================
// FILE EXPORT TYPES - Centralized Type System
// ============================================================================

/**
 * UI-layer export format (user selection)
 * Used in: ExportDropdown, ExportModal, all export hooks
 */
export type UIExportFormat = 'EXCEL' | 'PDF' | 'WORD';

/**
 * API-layer export format (backend communication)
 * Different casing convention for backend compatibility
 */
export type APIExportFormat = 'Excel' | 'PDF' | 'Word';

/**
 * File extension types
 */
export type FileExtension = 'xlsx' | 'pdf' | 'docx';

/**
 * MIME type for blob creation and validation
 */
export type MIMEType =
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // .xlsx
  | 'application/pdf' // .pdf
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
  | 'application/vnd.ms-excel' // Legacy .xls
  | 'application/msword'; // Legacy .doc

/**
 * Complete file format metadata
 */
export interface FileFormatMetadata {
  uiFormat: UIExportFormat;
  apiFormat: APIExportFormat;
  extension: FileExtension;
  mimeType: MIMEType;
  label: string;
}

/**
 * Format mapping registry (single source of truth)
 */
export const FILE_FORMAT_REGISTRY: Record<UIExportFormat, FileFormatMetadata> = {
  EXCEL: {
    uiFormat: 'EXCEL',
    apiFormat: 'Excel',
    extension: 'xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    label: 'Excel',
  },
  PDF: {
    uiFormat: 'PDF',
    apiFormat: 'PDF',
    extension: 'pdf',
    mimeType: 'application/pdf',
    label: 'PDF',
  },
  WORD: {
    uiFormat: 'WORD',
    apiFormat: 'Word',
    extension: 'docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    label: 'Word',
  },
};

/**
 * Helper functions (type-safe)
 */
export function getAPIFormat(uiFormat: UIExportFormat): APIExportFormat {
  return FILE_FORMAT_REGISTRY[uiFormat].apiFormat;
}

export function getFileExtension(uiFormat: UIExportFormat): FileExtension {
  return FILE_FORMAT_REGISTRY[uiFormat].extension;
}

export function getMIMEType(uiFormat: UIExportFormat): MIMEType {
  return FILE_FORMAT_REGISTRY[uiFormat].mimeType;
}

export function getFormatLabel(uiFormat: UIExportFormat): string {
  return FILE_FORMAT_REGISTRY[uiFormat].label;
}

/**
 * Resultado de una exportación híbrida (patrón sync/async del backend).
 * - `sync`: el backend respondió 200 con el archivo (blob) listo para descargar.
 * - `async`: el backend respondió 202 y encoló un job en background (identificado por `jobId`).
 * Forma canónica compartida por todos los export híbridos (marcajes, auditoría, incidencias).
 */
export type HybridExportResult =
  | { type: 'sync'; blob: Blob; filename: string }
  | { type: 'async'; jobId: string };

/**
 * Domain-specific format availability
 */
export type AbsenceExportFormat = Extract<UIExportFormat, 'EXCEL' | 'PDF' | 'WORD'>;
export type EmployeeExportFormat = Extract<UIExportFormat, 'EXCEL'>;
export type AttendanceExportFormat = Extract<UIExportFormat, 'EXCEL' | 'PDF' | 'WORD'>;
export type OvertimeExportFormat = Extract<UIExportFormat, 'EXCEL' | 'PDF' | 'WORD'>;
export type IncidentExportFormat = Extract<UIExportFormat, 'EXCEL'>;
